/**
 * **D333 — a device-local MIRROR of the account's locale, read by exactly one surface: the blocked
 * screen.**
 *
 * `locale` is cloud-owned (`User.locale`, `PATCH /users/me`) and `AuthProvider` reconciles it on
 * every boot, so the UI's language is known only once the session has loaded. D330's blocked
 * screen is mounted **instead of** `AuthProvider` — a hung auth boot must not stand between a
 * below-floor resident and the update — so before this mirror it could only render
 * `DEFAULT_LOCALE`, and a resident who chose English, Russian, Italian or Ukrainian read Hebrew on
 * the one screen whose whole job is to be understood. The cached profile already holds the locale,
 * but in IndexedDB, behind an async read the blocked screen is forbidden to depend on (it reads no
 * device cache — D330's third rule). `localStorage` is readable synchronously at boot, which is the
 * same reason D328 put the config document there.
 *
 * **A mirror, never a second owner.** The server stays authoritative and nothing that writes to it
 * reads this value. It is written from one place — `AuthProvider`'s reconcile effect, whenever the
 * session carries an account locale — so it tracks **the account's** language, never the UI's
 * default: a Hebrew account that never triggers a `setLocale` still overwrites an English value an
 * earlier account left behind. It is read from one place — `main.tsx`, and **only when the boot
 * read has put the device below the floor**. Seeding every launch's first paint from it is a
 * different decision (D333 (e)): the pre-sign-in UI's locale is what `POST /auth/otp/start` sends
 * as the SMS language and what a new account's name step stores as its `User.locale`, so a mirror
 * read everywhere would let device-local state write a cloud-owned setting.
 *
 * **Not cleared on sign-out, account switch or erasure.** It holds one language code and no
 * identifier, and the blocked screen reads no session — a signed-out device below the floor is
 * better served by the last language used on it than by the default. 🔸 The stated cost: on a
 * handset shared across accounts, the blocked screen speaks whichever language the last account
 * chose.
 *
 * Every read and write is wrapped — a private-mode browser throws on `getItem` rather than
 * returning `null` (`auth/credentials.ts`'s rule) — and a missing, refused or unrecognised value
 * reads as `DEFAULT_LOCALE`, which is exactly what the screen rendered before this existed. The
 * narrowing is `isLocale()` and nothing else (D200): a sixth locale reaches this mirror with no
 * edit here.
 */

import { STAGE } from '../auth/config'
import { DEFAULT_LOCALE, isLocale, type Locale } from './locales'

export const localeMirrorKey = (stage: string): string => `selogate.${stage}.locale`

/** The mirrored account locale, or `DEFAULT_LOCALE`. **Never throws.** */
export function readLocaleMirror(stage: string = STAGE): Locale {
  try {
    const raw = globalThis.localStorage?.getItem(localeMirrorKey(stage))
    return isLocale(raw) ? raw : DEFAULT_LOCALE
  } catch {
    return DEFAULT_LOCALE
  }
}

/** Mirrors a server-accepted locale. **Never throws** — a lost write costs one screen's language. */
export function writeLocaleMirror(locale: Locale, stage: string = STAGE): void {
  try {
    globalThis.localStorage?.setItem(localeMirrorKey(stage), locale)
  } catch {
    /* ignore — see readLocaleMirror */
  }
}
